import { Component, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { filter, switchMap } from 'rxjs/operators';
import {
  FlashCardModel,
  FlashCardStackModel,
  FlashCardStackQuery,
} from 'src/app/api/models';
import { FlashCardStacksService } from 'src/app/api/services';
import { DialogService } from 'src/app/cms/common/dialog.service';
import { ListActionMeta, ListConfig, ListQuery } from './list.types';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute, Router } from '@angular/router';
import { PanelService } from '../panel/panel.service';
import * as fileSaver from 'file-saver';
import { cloneDeep } from 'lodash';
import { Panel, PanelEntity, PanelEntityType } from '../panel/panel.types';
import {
  GridDataComponent,
  multiSelectAction,
} from './grid-data/grid-data.component';
import { EditService } from '../edit/edit.service';
import { StackEditConfigService } from '../edit/configs/stack.service';
import {
  AmplitudeSource,
  SourceType,
  UIEventEnum,
} from 'src/app/utility/amplitude-tracking/tracking.types';
import { validateSchema } from 'src/app/utility/app.utils';
import { schemas } from 'src/app/payload-validators';
import { FlashCardStackPanel } from '../panel/panels/flash-card-stack-panel';
import { FlashCardPanel } from '../panel/panels/flash-card-panel';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { UtilityService } from 'src/app/utility/utility.service';
import { AIImportDialogComponentData } from '../common/dialogs/ai-import-dialog/ai-import-dialog.component';

@Component({
  selector: 'qa-flash-card-stack-list',
  templateUrl: './flash-card-stack-list.component.html',
  styleUrls: ['./flash-card-stack-list.component.scss'],
  imports: [GridDataComponent],
})
export class FlashCardStackListComponent implements OnInit {
  config: ListConfig<FlashCardStackModel>;

  constructor(
    private flashCardStacks: FlashCardStacksService,
    private dialog: DialogService,
    private panelService: PanelService,
    private matDialog: MatDialog,
    private matSnackBar: MatSnackBar,
    private translator: TranslateService,
    private editService: EditService,
    private router: Router,
    private trackingService: TrackingService,
    private utilityService: UtilityService,
    flashCardStackPanel: FlashCardStackPanel,
    flashCardPanel: FlashCardPanel,
    route: ActivatedRoute
  ) {
    if (!panelService?.panels.length) {
      panelService.init([flashCardStackPanel, flashCardPanel], route);
    }
  }

  ngOnInit() {
    const flashCardPanelEntity = this.panelService.panels.find(
      (panel) => panel.type === PanelEntityType.FlashCardStack
    );
    const config = getFlashCardStackListConfig(
      this.flashCardStacks,
      this.matDialog,
      this.dialog,
      this.matSnackBar,
      this.translator,
      this.router,
      true,
      this.editService,
      this.panelService,
      flashCardPanelEntity,
      this.trackingService,
      this.utilityService
    );
    this.config = config;
  }
}

export function getFlashCardStackListConfig(
  flashCardStacks: FlashCardStacksService,
  matDialog: MatDialog,
  dialog: DialogService,
  matSnackBar: MatSnackBar,
  translator: TranslateService,
  router: Router,
  showMultiselectActions: boolean,
  editService: EditService,
  panelService?: PanelService,
  flashCardPanel?: Panel<PanelEntity>,
  trackingService?: TrackingService,
  utilityService?: UtilityService
): ListConfig<FlashCardStackModel> {
  async function importCards(
    items: FlashCardModel[],
    parentId: number,
    dataSource
  ) {
    if (items?.length) {
      await flashCardStacks
        .copyCardsToFlashCardStack({ id: parentId, body: items })
        .toPromise();
    }
    dataSource.refresh();
    utilityService?.triggerRefresh();
  }

  async function exportAsJson(meta: ListActionMeta<FlashCardStackModel>) {
    meta.dataSource.loading = true;
    let fullItem: FlashCardStackModel;
    fullItem = cloneDeep(meta.row);
    const cards = await flashCardStacks
      .getCardsInFlashCardStack({ id: fullItem.id })
      .toPromise();
    cards.forEach((c) => delete c.id);
    delete fullItem.id;
    fullItem.cards = cards;
    fullItem['meta:importType'] = PanelEntityType.FlashCardStack;
    const file = `${JSON.stringify(fullItem)}\n`;
    const blob = new Blob([file], { type: 'application/json' });
    fileSaver.saveAs(
      blob,
      `qa-${PanelEntityType.FlashCardStack}-${fullItem.name}.json`
    );
    trackingService.trackEvent({
      event_type: UIEventEnum.JSON_EXPORTED,
      event_properties: {
        source: AmplitudeSource.STACK,
      },
    });
    meta.dataSource.loading = false;
  }

  const ret: ListConfig<FlashCardStackModel> = {
    actions: [
      {
        click: ({ row, dataSource }) => {
          dialog
            .confirmDelete()
            .pipe(
              filter((isConfirmed) => isConfirmed),
              switchMap(() =>
                flashCardStacks.deleteFlashCardStack({ id: row.id })
              )
            )
            .subscribe({
              next: () => {
                matSnackBar.open(
                  translator.instant('flashCardStack.message.deleteSuccess')
                );
                dataSource.refresh();
                utilityService?.triggerRefresh();
              },
              error: () => {
                matSnackBar.open(
                  translator.instant('flashCardStack.message.deleteFail')
                );
              },
            });
        },
        icon: 'delete',
        name: 'delete',
        tooltip: 'flashCardStack.tooltip.delete',
        type: 'button',
      },
    ],
    actionsMenu: [
      {
        icon: 'save_alt',
        name: 'download',
        label: translator.instant(
          'content.flashCardStack.actionMenu.downloadJSON'
        ),
        type: 'button',
        click: (meta) => exportAsJson(meta),
      },
      {
        icon: 'description',
        type: 'button',
        name: 'export_csv',
        label: translator.instant('content.exam.actionMenu.downloadCSV'),
        click: (meta) => {
          panelService.exportItemAsCSV(flashCardPanel, meta.row.id);
        },
      },
    ],
    activeActionNames: ['edit', 'cards', 'delete'],
    activeColumnNames: ['id', 'name', 'num_cards'],
    columns: [
      {
        label: 'ID',
        name: 'id',
        type: 'number',
        sortable: true,
        width: '64px',
        click: (meta) =>
          router.navigateByUrl(`/cms/content/${meta.row.id}/flash-cards`),
        filter: (value) => {
          return {
            id: Number(value),
          };
        },
      },
      {
        click: (meta) =>
          router.navigateByUrl(`/cms/content/${meta.row.id}/flash-cards`),
        label: 'Name',
        name: 'name',
        sortable: true,
        primary: true,
        filter: (value) => {
          return {
            name: value,
          };
        },
      },
      {
        label: 'common.label.quizFlashCard',
        name: 'num_cards',
        sortable: true,
        width: '200px',
        click: (meta) =>
          router.navigateByUrl(`/cms/content/${meta.row.id}/flash-cards`),
        filter: (value) => {
          return {
            num_cards: Number(value),
          };
        },
      },
    ],
    wizardImports: {
      createWithAI: {
        text: translator.instant('content.wizardActions.title.createWithAI'),
        icon: 'auto_awesome',
        description: translator.instant(
          'content.wizardActions.description.createStackWithAI'
        ),
        tag: translator.instant('content.wizardActions.tag.createWithAI'),
        click: async (dataSource) => {
          const stack = await editService.open(StackEditConfigService);
          dataSource?.refresh();
          if (stack && stack?.id) {
            router
              .navigateByUrl(`/cms/content/${stack.id}/flash-cards`)
              .then(() => {
                matDialog.closeAll();
                const dialogData: AIImportDialogComponentData = {
                  name: null,
                  type: null,
                  onImport: () => {
                    const queryParams = {
                      type: PanelEntityType.FlashCard,
                    };
                    panelService.redirectToImportItems(
                      `/cms/csv-imports/flash-card/${stack.id}`,
                      queryParams,
                      true
                    );
                    matDialog.closeAll();
                  },
                };
                panelService.openChatGprDialog(dialogData);
              });
          }
          matDialog.closeAll();
        },
      },
      createButton: {
        text: translator.instant('content.wizardActions.title.createManually'),
        icon: 'edit',
        description: translator.instant(
          'content.wizardActions.description.createManually'
        ),
        click: async (dataSource) => {
          await editService.open(StackEditConfigService);
          matDialog.closeAll();
          dataSource?.refresh();
        },
      },
      importButtons: [
        {
          text: translator.instant(
            'content.wizardActions.title.importFromJSON'
          ),
          description: translator.instant(
            'content.wizardActions.description.importFromJSON'
          ),
          icon: 'save_alt',
          click: async (dataSource) => {
            const item = (await panelService.promptFile(
              'json'
            )) as FlashCardStackModel;
            matDialog.closeAll();
            const sentryInfo = {
              title: `Error while importing flash card-stack`,
              file: 'flash-card-stack-list.component.ts',
              api: 'createFlashCardStack',
              preventLogs: true,
            };
            const validateQuestionsPayload = validateSchema(
              schemas.FlashCardStackModel,
              item,
              matSnackBar,
              sentryInfo
            );
            if (validateQuestionsPayload?.isInvalidSchema) {
              return;
            }
            dataSource.loading = true;
            flashCardStacks
              .createFlashCardStack({
                body: item,
                source: SourceType.JSON_IMPORT,
              })
              .subscribe({
                next: (stack) => {
                  const sentryInfo = {
                    title: `Error while importing stack cards from JSON`,
                    file: 'flash-card-stack-list.component.ts - import from JSON',
                    api: 'copyCardsToFlashCardStack',
                    preventLogs: true,
                  };
                  const validateCardsPayload = validateSchema(
                    schemas.FlashCardModel.array(),
                    item?.cards,
                    matSnackBar,
                    sentryInfo
                  );
                  if (validateCardsPayload?.isInvalidSchema) {
                    dataSource.loading = false;
                    return;
                  }
                  importCards(item?.cards, stack.id, dataSource);
                },
                error: () => (dataSource.loading = false),
              });
          },
        },
      ],
    },
    createButton: {
      options: [
        {
          text: translator.instant(
            'content.flashCardStack.addMenu.createNewStack'
          ),
          icon: 'add',
          click: async (dataSource) => {
            await editService.open(StackEditConfigService);
            dataSource?.refresh();
          },
        },
      ],
    },
    imports: [
      {
        title: translator.instant(
          'content.flashCardStack.title.importStackFromJSON'
        ),
        description: translator.instant(
          'content.flashCardStack.description.importStackFromJSON'
        ),
        action: {
          icon: 'code',
          click: async (dataSource) => {
            const item = (await panelService.promptFile(
              'json'
            )) as FlashCardStackModel;
            matDialog.closeAll();
            const sentryInfo = {
              title: `Error while importing flash card-stack`,
              file: 'flash-card-stack-list.component.ts',
              api: 'createFlashCardStack',
              preventLogs: true,
            };
            const validateQuestionsPayload = validateSchema(
              schemas.FlashCardStackModel,
              item,
              matSnackBar,
              sentryInfo
            );
            if (validateQuestionsPayload?.isInvalidSchema) {
              return;
            }
            dataSource.loading = true;
            flashCardStacks
              .createFlashCardStack({
                body: item,
                source: SourceType.JSON_IMPORT,
              })
              .subscribe({
                next: (stack) => {
                  const sentryInfo = {
                    title: `Error while importing stack cards from JSON`,
                    file: 'flash-card-stack-list.component.ts - import from JSON',
                    api: 'copyCardsToFlashCardStack',
                    preventLogs: true,
                  };
                  const validateCardsPayload = validateSchema(
                    schemas.FlashCardModel.array(),
                    item?.cards,
                    matSnackBar,
                    sentryInfo
                  );
                  if (validateCardsPayload?.isInvalidSchema) {
                    dataSource.loading = false;
                    return;
                  }
                  importCards(item?.cards, stack.id, dataSource);
                },
                error: () => (dataSource.loading = false),
              });
          },
          text: translator.instant(
            'content.flashCardStack.addMenu.importStackFromJSON'
          ),
        },
      },
    ],
    fetch: (queryParams?: ListQuery<FlashCardStackModel>) => {
      const query = queryParams as unknown as FlashCardStackQuery;
      return flashCardStacks.queryFlashCardStack({ body: query });
    },
    query: {
      initial: {
        sorting: [
          {
            option: 'id',
            direction: 'DESC',
          },
        ],
      },
      searchColumnName: 'name',
    },
    name: 'flashCardStackList',
    title: translator.instant('common.label.flashCardStack'),
  };

  if (showMultiselectActions) {
    ret.multiselectActions = [
      {
        icon: 'delete',
        name: 'question.tooltip.delete',
        type: 'button',
        click: (meta) => {
          return new Promise((resolve) => {
            dialog
              .confirmDelete(
                null,
                translator.instant('common.message.deleteMulti', {
                  count: meta.rows.length,
                })
              )
              .pipe(filter((isConfirmed) => isConfirmed))
              .subscribe(async () => {
                await multiSelectAction(
                  meta.rows,
                  async (item: any) => {
                    await flashCardStacks
                      .deleteFlashCardStack({ id: item.id })
                      .toPromise();
                    return true;
                  },
                  `${translator.instant(
                    'common.label.deleting'
                  )} ${translator.instant('common.label.flashCardStack')}`,
                  matDialog
                );
                meta.dataSource.refresh();
                utilityService?.triggerRefresh();
                resolve();
              });
          });
        },
      },
    ];
  }

  return ret;
}
